import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { uploadAttractionImage } from '~/services/AttractionsService';

import type { Attraction } from '~/types/attractionTypes';

import { setCacheBuster } from './attractionCacheBuster';
import { attractionQueryKeys } from './attractionQueryKeys';

export const ACCEPTED_IMAGE_TYPES = 'image/jpeg, image/png, image/webp';
export const ACCEPTED_IMAGE_DROPZONE_TYPES = {
  'image/jpeg': [],
  'image/png': [],
  'image/webp': [],
};
export const MAX_IMAGE_SIZE = 10 * 1024 * 1024;

export function useUploadAttractionImage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ attractionId, file }: { attractionId: string; file: File }) => {
      return await uploadAttractionImage(attractionId, file);
    },
    onSuccess: (data, { attractionId }) => {
      if (data.updatedAt) {
        setCacheBuster(attractionId, data.updatedAt);
      }

      if (data.imageUrl) {
        queryClient.setQueryData<Attraction>(attractionQueryKeys.detail(attractionId), (prev) =>
          prev ? { ...prev, imageUrl: data.imageUrl, imageCurated: true } : prev,
        );
      }

      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.detail(attractionId) });
      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.images(attractionId) });
      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.lists() });

      enqueueSnackbar('Custom image uploaded successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Upload failed: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
  });
}
